import {baseUri} from '@jetbrains/teamcity-ui'

import type {ActionCreator} from '../actions/types'

import {
  cloneNode,
  getBuildTypeStatusRequest,
  getPipelineStatusRequest,
  getProjectStatusRequest,
  stringifyId,
  toFederationServerId,
} from '.'
import type {
  BuildTypeId,
  BuildTypeInternalId,
  TypeOfBuildType,
  CustomSidebarItemId,
  FederationServerId,
  NodeGroup,
  ProjectId,
  ProjectInternalId,
  ProjectOrBuildTypeNode,
  StatusRequest,
  TitleId,
  PipelineId,
} from '.'

type SearchStringMatch = {
  index: number
  length: number
}
type ProjectsTreeItemCommon = {
  archived?: boolean
  paused?: boolean
  name: string
  type?: TypeOfBuildType | null | undefined
  level: number
  hasChildren?: boolean
  childrenProjectsIds?: ReadonlyArray<ProjectId>
  childrenPipelinesIds?: ReadonlyArray<PipelineId>
  childrenBuildTypesIds?: ReadonlyArray<BuildTypeId> | null | undefined
  expanded?: boolean
  group: NodeGroup
  searchMatches?: ReadonlyArray<SearchStringMatch> | null | undefined
  isFullMatch?: boolean
  isStarred?: boolean | null | undefined
  serverId: FederationServerId
  icon?: string | null | undefined
  editable?: boolean
  focusable?: boolean
  isDisabled?: boolean
  hasReorderedChildren?: boolean
  /**
   * Info: Performance improvement
   * Added `key` field for creating a deferred  hashmap by the object getter
   */
  key?: string
  /**
   * Info: Performance improvement
   * Added the `index` field of the index of the item on the Map,
   * an easy way to understand the index of the item without extra looping
   */
  index?: number
  noSettingsAccess?: boolean
}
export type ProjectsTreeItemBuildType = {
  itemType: 'buildType'
  id: BuildTypeId
  internalId: BuildTypeInternalId | null | undefined
  parentId: ProjectId
  parentInternalId: ProjectInternalId
  actualParentId?: ProjectId | null | undefined
} & ProjectsTreeItemCommon
type ProjectsTreeItemPipeline = {
  itemType: 'pipeline'
  id: PipelineId
  internalId: ProjectInternalId | undefined
  parentId: ProjectId
  actualParentId?: ProjectId | null | undefined
} & ProjectsTreeItemCommon
export type ProjectTreeItemProject = {
  itemType: 'project'
  id: ProjectId
  internalId: ProjectInternalId
  parentId?: ProjectId | null | undefined
  actualParentId?: ProjectId | null | undefined
  isReadOnly: boolean
} & ProjectsTreeItemCommon
type ProjectTreeItemTitle = {
  itemType: 'title'
  id: TitleId
  internalId?: never
  parentId?: never
} & ProjectsTreeItemCommon
export type ProjectsTreeItem =
  | ProjectsTreeItemBuildType
  | ProjectsTreeItemPipeline
  | ProjectTreeItemProject
  | ProjectTreeItemTitle
  | ({
      itemType: 'server'
      id: FederationServerId
      internalId?: never
      parentId?: never
      authorized: boolean
      loading: boolean
    } & ProjectsTreeItemCommon)
  | ({
      itemType: 'link'
      id: CustomSidebarItemId
      internalId?: never
      parentId?: never
      href: string
    } & ProjectsTreeItemCommon)
  | ({
      itemType: 'action'
      id: CustomSidebarItemId
      internalId?: never
      parentId?: never
      action: ActionCreator | string
    } & ProjectsTreeItemCommon)
export const getProjectTreeItemId = (item: ProjectsTreeItem): string =>
  `${stringifyId(item.serverId)}_${item.group}_${item.itemType}_${stringifyId(item.id)}`
export const getProjectTreeItemStatusRequest = (
  item: ProjectsTreeItem,
): StatusRequest | null | undefined => {
  switch (item.itemType) {
    case 'project':
      return getProjectStatusRequest(item.id)

    case 'buildType':
      return getBuildTypeStatusRequest(item.id)

    case 'pipeline':
      return getPipelineStatusRequest(item.id)

    default:
      return null
  }
}
export type SidebarActiveItem =
  | ProjectOrBuildTypeNode
  | {
      nodeType: 'link'
      id: CustomSidebarItemId
      group?: never
    }
export const cloneActiveItem = (node: SidebarActiveItem): SidebarActiveItem => {
  switch (node.nodeType) {
    case 'link':
      return {
        nodeType: 'link',
        id: node.id,
      }

    default:
      return cloneNode(node)
  }
}
export function getNodeFromItem(item: ProjectsTreeItem): ProjectOrBuildTypeNode | null | undefined {
  switch (item.itemType) {
    case 'pipeline': {
      const {id, parentId} = item
      return {
        nodeType: 'pipeline',
        id,
        parentId,
      }
    }

    case 'buildType': {
      const {id, internalId, parentId, parentInternalId} = item
      return {
        nodeType: 'bt',
        id,
        internalId,
        parentId,
        parentInternalId,
      }
    }

    case 'project': {
      const {id, internalId, parentId} = item
      return {
        nodeType: 'project',
        id,
        internalId,
        parentId,
      }
    }

    default:
      return null
  }
}
export const currentServerId: FederationServerId = toFederationServerId(baseUri)
